/**
 * Stamp coordinates for the AMS lease PDF, mirroring the layout produced by
 * scripts/generate-lease-pdf.ts. PDF coordinates are bottom-left origin, in
 * PDF points (1pt = 1/72in).
 *
 * If you change layout in the generator, regenerate the PDF, copy the
 * resulting placements JSON values here, and re-upload the template.
 */

export interface FieldCoords {
  pageIndex: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  fontSize?: number;
}

export interface LeasePlacements {
  // Page 1 — top client info table (filled from collected form fields)
  page1TopClientName: FieldCoords;
  page1TopDob: FieldCoords;
  page1TopAddress: FieldCoords;
  page1TopPhone: FieldCoords;
  // Page 1 — acknowledgement / signature block
  page1ClientName: FieldCoords;
  page1Signature: FieldCoords;
  page1Date: FieldCoords;
  // Page 2 — "effective as of ___" + lessee name
  page2EffectiveDate: FieldCoords;
  page2LesseeName: FieldCoords;
  // Page 4 — final lessee signature block + "and by ___"
  page4AndByName: FieldCoords;
  page4Signature: FieldCoords;
  page4Date: FieldCoords;
  // Page 5 — Exhibit A fields
  page5ClientName: FieldCoords;
  page5ClientAddress: FieldCoords;
  page5ClientZip: FieldCoords;
  /** Replaces the old SSN slot. Stamp is conditional on caseNumber truthy. */
  page5CaseNumber: FieldCoords;
  page5Email: FieldCoords;
  page5ProbationOfficer: FieldCoords;
  page5WorkEmployer: FieldCoords;
  page5WorkAddress: FieldCoords;
  page5WorkPhone: FieldCoords;
  page5Supervisor: FieldCoords;
  page5SupervisorPhone: FieldCoords;
  // Known contacts (replaces legacy spouse rows). 1st contact = top row,
  // 2nd contact = ~22pt below.
  page5Contact1Name: FieldCoords;
  page5Contact1Phone: FieldCoords;
  page5Contact1Relationship: FieldCoords;
  page5Contact2Name: FieldCoords;
  page5Contact2Phone: FieldCoords;
  page5Contact2Relationship: FieldCoords;
}

/**
 * Source of truth for these values is scripts/generate-lease-pdf.ts (for the
 * acknowledgement / payment-terms regions) and scripts/test-stamp-lease.ts
 * (for the original-layout fields like the page-1 top table and Exhibit A).
 *
 * Coordinates are PDF points, bottom-left origin (1pt = 1/72in).
 */
export const LEASE_PLACEMENTS: LeasePlacements = {
  // Page 1 top client info table
  page1TopClientName: { pageIndex: 0, x: 120, y: 680, fontSize: 10 },
  page1TopDob: { pageIndex: 0, x: 345, y: 680, fontSize: 10 },
  page1TopAddress: { pageIndex: 0, x: 95, y: 660, fontSize: 10 },
  page1TopPhone: { pageIndex: 0, x: 345, y: 660, fontSize: 10 },
  // Page 1 acknowledgement block (rewritten in generator)
  page1ClientName: { pageIndex: 0, x: 62, y: 388, fontSize: 10 },
  page1Signature: { pageIndex: 0, x: 90, y: 350, width: 265, height: 22 },
  page1Date: { pageIndex: 0, x: 415, y: 352, fontSize: 10 },
  // Page 2 lessee-name + effective-date blanks
  page2EffectiveDate: { pageIndex: 1, x: 340, y: 682, fontSize: 10 },
  page2LesseeName: { pageIndex: 1, x: 85, y: 656, fontSize: 10 },
  // Page 4 signatories paragraph + signature block (rewritten in generator)
  page4AndByName: { pageIndex: 3, x: 185, y: 397, fontSize: 10 },
  page4Signature: { pageIndex: 3, x: 82, y: 130, width: 210, height: 22 },
  page4Date: { pageIndex: 3, x: 335, y: 132, fontSize: 10 },
  // Page 5 — Exhibit A rows
  page5ClientName: { pageIndex: 4, x: 45, y: 517, fontSize: 10 },
  page5ClientAddress: { pageIndex: 4, x: 185, y: 517, fontSize: 10 },
  page5ClientZip: { pageIndex: 4, x: 325, y: 517, fontSize: 10 },
  // Old SSN slot, repurposed for the optional case number.
  page5CaseNumber: { pageIndex: 4, x: 460, y: 517, fontSize: 10 },
  page5Email: { pageIndex: 4, x: 45, y: 482, fontSize: 10 },
  page5ProbationOfficer: { pageIndex: 4, x: 400, y: 482, fontSize: 9 },
  page5WorkEmployer: { pageIndex: 4, x: 45, y: 412, fontSize: 10 },
  page5WorkAddress: { pageIndex: 4, x: 185, y: 412, fontSize: 10 },
  page5WorkPhone: { pageIndex: 4, x: 460, y: 412, fontSize: 10 },
  page5Supervisor: { pageIndex: 4, x: 45, y: 347, fontSize: 10 },
  page5SupervisorPhone: { pageIndex: 4, x: 200, y: 347, fontSize: 10 },
  // Known contact rows (reuse the old spouse layout; we now stamp two
  // contacts ~22pt apart).
  page5Contact1Name: { pageIndex: 4, x: 45, y: 237, fontSize: 10 },
  page5Contact1Phone: { pageIndex: 4, x: 260, y: 237, fontSize: 10 },
  page5Contact1Relationship: { pageIndex: 4, x: 420, y: 237, fontSize: 10 },
  page5Contact2Name: { pageIndex: 4, x: 45, y: 215, fontSize: 10 },
  page5Contact2Phone: { pageIndex: 4, x: 260, y: 215, fontSize: 10 },
  page5Contact2Relationship: { pageIndex: 4, x: 420, y: 215, fontSize: 10 },
};

export function formatDate(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}
